/**
 * Mag Academy–style YouTube embed helpers
 * Matches Mag embed params:
 * autoplay=0&controls=0&disablekb=1&playsinline=1&cc_load_policy=0&cc_lang_pref=auto
 * &widget_referrer=…&rel=0&showinfo=0&iv_load_policy=3&modestbranding=1
 * &customControls=true&noCookie=false&enablejsapi=1&origin=…
 */

export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  try {
    const trimmed = url.trim();

    const embedMatch = trimmed.match(/youtube\.com\/embed\/([\w-]{11})/);
    if (embedMatch) return embedMatch[1];

    const shortMatch = trimmed.match(/youtu\.be\/([\w-]{11})/);
    if (shortMatch) return shortMatch[1];

    const watchMatch = trimmed.match(/[?&]v=([\w-]{11})/);
    if (watchMatch) return watchMatch[1];

    const vMatch = trimmed.match(/youtube\.com\/v\/([\w-]{11})/);
    if (vMatch) return vMatch[1];

    if (/^[\w-]{11}$/.test(trimmed)) return trimmed;
  } catch {
    return null;
  }

  return null;
}

/** Normalize any YouTube URL / id to a watch URL for noembed */
export function toYouTubeWatchUrl(videoIdOrUrl: string): string | null {
  const videoId = extractYouTubeVideoId(videoIdOrUrl);
  if (!videoId) return null;
  return `https://www.youtube.com/watch?v=${videoId}`;
}

export interface YouTubeEmbedOptions {
  autoplay?: boolean;
  origin?: string;
  widgetReferrer?: string;
}

/** Mag-style playerVars for Plyr / YT.Player (origin filled at runtime). */
export function getMagYouTubePlayerVars(options: YouTubeEmbedOptions = {}) {
  const origin =
    options.origin ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  const widgetReferrer =
    options.widgetReferrer ||
    (typeof window !== 'undefined' ? `${window.location.origin}/` : '');

  return {
    autoplay: options.autoplay ? 1 : 0,
    controls: 0,
    disablekb: 1,
    playsinline: 1,
    cc_load_policy: 0,
    cc_lang_pref: 'auto',
    widget_referrer: widgetReferrer,
    rel: 0,
    showinfo: 0,
    iv_load_policy: 3,
    modestbranding: 1,
    customControls: true,
    noCookie: false,
    enablejsapi: 1,
    ...(origin
      ? {
          origin,
          aorigins: origin,
          gporigin: `${origin}/`,
        }
      : {}),
  } as const;
}

/**
 * Build a Mag Academy–style YouTube embed URL
 * Example shape:
 * https://www.youtube.com/embed/VIDEO_ID?autoplay=0&controls=0&disablekb=1&…
 */
export function buildYouTubeEmbedUrl(
  videoIdOrUrl: string,
  options: YouTubeEmbedOptions = {}
): string | null {
  const videoId = extractYouTubeVideoId(videoIdOrUrl);
  if (!videoId) return null;

  const vars = getMagYouTubePlayerVars(options);
  const params = new URLSearchParams();

  Object.entries(vars).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    params.set(key, String(value));
  });

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}
